// Each group = one section. `section: null` renders with no section header row.
// `type: "text"` shows the raw string; `type: "boolean"` renders ✓ / ✗.
// `values` keys must match plan.planName exactly ("Standard" | "Professional" | "Enterprise").
//
// Data is keyed by PRODUCT_CONTEXT ("em" | "ra" | "am").

const emFeaturesData = [
  {
    section: null,
    rows: [
      {
        key: "devices",
        label: "Devices",
        info: "Maximum number of endpoint devices that can be managed under this plan.",
        type: "text",
        values: {
          Standard: "> 15 Endpoints",
          Professional: "> 15 Endpoints",
          Enterprise: "> 15 Endpoints",
        },
      },
      {
        key: "technician",
        label: "Technician",
        info: "Number of technicians that can access and manage the platform.",
        type: "text",
        values: {
          Standard: "> 1 Tech",
          Professional: "> 1 Tech",
          Enterprise: "> 1 Tech",
        },
      },
    ],
  },
  {
    section: "Enrollment",
    rows: [
      {
        key: "manualDeployment",
        label: "Manual Deployment",
        info: "Manually install the agent on individual devices.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "deploymentLink",
        label: "Deployment Link",
        info: "Shareable link to initiate device enrollment remotely.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "domainAuth",
        label: "Domain Authentication",
        info: "Use domain user credentials to authenticate and enroll devices via Zecurit Connector",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "bulkDeployment",
        label: "Bulk Deployment (Entra ID, GPO, SCCM, Intune)",
        info: "Perform bulk agent deployment across multiple devices using enterprise tools like Azure AD, GPO, SCCM or Intune.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
    ],
  },
  {
    section: "Inventory",
    rows: [
      {
        key: "assetDiscovery",
        label: "Asset Discovery",
        info: "Automatically scan and identify all connected hardware and software assets.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "softwareInventory",
        label: "Software Inventory Management",
        info: "View and manage a list of all installed software across devices.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "hardwareInventory",
        label: "Hardware Inventory Management",
        info: "Track and manage hardware components and configurations.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "softwareLicense",
        label: "Software License Management",
        info: "Monitor and track software license usage and compliance.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "softwareMetering",
        label: "Software Metering",
        info: "Monitor the frequency and duration of software usage to optimize licenses.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "blacklistWhitelist",
        label: "Blacklist and Whitelist Software",
        info: "Define and manage restricted (blacklisted) and allowed (whitelisted) software.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "softwareCategory",
        label: "Software Category",
        info: "Classify software based on type (e.g., productivity, security, games).",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "geoLocationTracking",
        label: "Geo Location Tracking",
        info: "Track the physical location of enrolled devices.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "alertsPolicy",
        label: "Alerts Policy",
        info: "Configure rules to trigger alerts based on inventory or policy violations.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "scheduleScan",
        label: "Schedule Scan",
        info: "Perform schedule device scanning at predefined intervals for asset updates.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
    ],
  },
  {
    section: "Vulnerability & Risk Management",
    rows: [
      {
        key: "vulnerabilityManagement",
        label: "Vulnerability Management",
        info: "Detect, prioritize, and track software and OS vulnerabilities across managed endpoints.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
      {
        key: "highRiskSoftware",
        label: "High Risk Software Detection",
        info: "Identify software with known high-risk vulnerabilities or end-of-life status.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
      {
        key: "portAudit",
        label: "Port Audit",
        info: "Audit open ports across endpoints to identify unnecessary exposure and risk. Included with Enterprise; feature launch coming soon.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
        comingSoon: { Enterprise: true },
      },
      {
        key: "zeroDayAlerts",
        label: "Zero-Day Vulnerability Alerts",
        info: "Receive proactive alerts on newly disclosed zero-day vulnerabilities affecting your environment.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
    ],
  },
  {
    section: "Patch Management",
    rows: [
      {
        key: "windowsUpdatePolicy",
        label: "Windows Update Policy",
        info: "Create and manage Windows update policies for automated patching.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "missingInstalledPatches",
        label: "Missing & Installed Patches",
        info: "View missing and installed patches for individual devices.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "orgLevelPatchViews",
        label: "Org Level Patch Views",
        info: "Organization-wide view of missing, installed, and available patches.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "patchDeployment",
        label: "Patch Deployment",
        info: "Deploy patches to selected devices or groups.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "automatedPatchDeployment",
        label: "Automated Patch Deployment",
        info: "Automatically deploy approved patches to devices based on policy, without manual intervention.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
      {
        key: "testApprovePatch",
        label: "Test and Approve Patch",
        info: "Test patches in a controlled group before approving them for org-wide deployment.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
    ],
  },
  {
    section: "Application & Device Control",
    rows: [
      {
        key: "applicationControl",
        label: "Application Control",
        info: "Allow, block, or restrict application execution across endpoints based on policy.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
      {
        key: "usbDeviceControl",
        label: "USB Device Control",
        info: "Control and restrict USB and peripheral device access on managed endpoints.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
    ],
  },
  {
    section: "Software Deployment",
    rows: [
      {
        key: "repoUploadPackage",
        label: "Software Repository - Upload Package",
        info: "Upload and store software packages in a centralized repository for deployment.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "repoNetworkShare",
        label: "Software Repository - Network Share",
        info: "Deploy software from network shared locations.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "repoDeploymentLink",
        label: "Software Repository - Deployment Link",
        info: "Generate deployment links for remote software installation.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "softwareDeploymentPolicy",
        label: "Software Deployment Policy (Windows/MAC/Linux)",
        info: "Create and manage automated software deployment policies across different operating systems.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
    ],
  },
  {
    section: "Remote Script Execution",
    rows: [
      {
        key: "scriptTemplate",
        label: "Script Template",
        info: "Pre-built script templates for common administrative tasks.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "scriptRepository",
        label: "Script Repository",
        info: "Store and manage custom scripts in a centralized repository.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "scriptPolicySchedule",
        label: "Script Policy for Schedule",
        info: "Schedule scripts to run automatically at defined intervals.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
    ],
  },
  {
    section: "Encryption Management",
    rows: [
      {
        key: "windowsBitlockerPolicy",
        label: "Windows Bitlocker Policy",
        info: "Configure and enforce BitLocker encryption policies on Windows devices.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
      {
        key: "macFileVaultPolicy",
        label: "Mac FileVault Policy",
        info: "Configure and enforce FileVault disk encryption policies on macOS devices.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
      {
        key: "linuxLuks2Policy",
        label: "Linux LUKS2 Encryption Policy",
        info: "Configure and enforce LUKS2 disk encryption policies on Linux devices.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
      {
        key: "bitlockerRecoveryReports",
        label: "Bitlocker, Recovery Key & TPM Reports",
        info: "View BitLocker status, recovery keys, and TPM information across devices.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
    ],
  },
  {
    section: "Power Management",
    rows: [
      {
        key: "windowsPowerPolicy",
        label: "Windows Power Policy",
        info: "Configure power settings and sleep policies for Windows devices.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "powerManagementReports",
        label: "Power Management Reports",
        info: "Reports on system uptime and user logon activity.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
    ],
  },
  {
    section: "Configuration Management",
    rows: [
      {
        key: "configurationPolicy",
        label: "Configuration Policy (Windows/MAC/Linux)",
        info: "Create and deploy configuration policies across different operating systems.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "configDeploymentStatusReports",
        label: "Configuration Deployment Status Reports",
        info: "Track the status and compliance of configuration deployments.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
    ],
  },
  {
    section: "Remote Desktop & Tools",
    rows: [
      {
        key: "remoteControl",
        label: "Remote Control (Unattended Remote Access)",
        info: "Take remote control of devices for troubleshooting and support.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "remoteSupport",
        label: "Remote Support (On-Demand Remote Assistance)",
        info: "Start secure on-demand remote sessions to troubleshoot issues, assist users, and resolve problems from anywhere.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "remoteCommandPrompt",
        label: "Remote Command Prompt",
        info: "Open a remote command prompt on the endpoint for troubleshooting.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "remoteTaskManager",
        label: "Remote Task Manager",
        info: "View and manage running processes on the endpoint remotely.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "remoteEventViewer",
        label: "Remote Event Viewer",
        info: "Inspect Windows event logs on the endpoint remotely.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "remoteServicesRegistry",
        label: "Remote Services & Remote Registry",
        info: "Manage Windows services and registry entries on the endpoint remotely.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "fileTransfer",
        label: "File Transfer",
        info: "Transfer files to and from remote devices during support sessions.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "chat",
        label: "Chat",
        info: "Chat with end users during remote sessions.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "sessionNotes",
        label: "Session Notes",
        info: "Add notes and documentation during remote support sessions.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "annotation",
        label: "Annotation",
        info: "Draw and annotate on the remote screen during support sessions.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "wakeOnLan",
        label: "Wake On Lan",
        info: "Remotely wake up sleeping or powered-off devices on the network.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "remoteShutdown",
        label: "Remote Shutdown",
        info: "Remotely shut down or restart devices.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "sessionHistory",
        label: "Session History",
        info: "View history of all remote support sessions.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "remoteControlRecording",
        label: "Remote Control Recording",
        info: "Record remote control and support sessions for audit and compliance purposes.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
    ],
  },
  {
    section: "Reports",
    rows: [
      {
        key: "securityReports",
        label: "Security Reports",
        info: "Generate insights on vulnerabilities, threats and compliance status.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "hardwareReports",
        label: "Hardware Reports",
        info: "Reports covering hardware configurations changes and statuses.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "softwareReports",
        label: "Software Reports",
        info: "Reports summarizing installed software and usage statistics.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "licenseReports",
        label: "License Reports",
        info: "Overview of license usage, expiry, and compliance across the organization.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "certificateReports",
        label: "Certificate Reports",
        info: "View certificates installed on devices and monitor expiry.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "userLogonReports",
        label: "User Logon Reports",
        info: "Track user login activity across managed devices.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "enrollmentReports",
        label: "Enrollment Reports",
        info: "Summary of device enrollment activity, including methods and statuses.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "powerReports",
        label: "Power Reports",
        info: "Analyze device power usage and sleep/wake patterns.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "complianceReports",
        label: "Compliance Reports",
        info: "Monitor device compliance status, identify policy violations, track security posture, and generate reports to support internal audits and regulatory requirements.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "customReports",
        label: "Custom Reports (Build Reports Your Way)",
        info: "Create tailored reports by selecting the data, filters, and columns that matter most to your organization, and export them for analysis and sharing.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "scheduleReports",
        label: "Schedule Reports",
        info: "Automate generation and delivery of reports at regular intervals.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "patchReports",
        label: "Patch Reports",
        info: "Detailed reporting on patch test, approval, and deployment status across the organization.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
      {
        key: "vulnerabilityReports",
        label: "Vulnerability Reports",
        info: "Reports summarizing detected vulnerabilities, risk levels, and remediation status across endpoints.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
    ],
  },
  {
    section: "Administration & Control",
    rows: [
      {
        key: "deviceGroups",
        label: "Device Groups (Static & Unique)",
        info: "Organize devices into groups for easier policy and task management.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "rebranding",
        label: "Rebranding",
        info: "Customize the agent interface and branding elements to reflect your organization.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "multiTechnician",
        label: "Multi-technician",
        info: "Allow multiple technicians to access the platform simultaneously.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "defaultRoles",
        label: "Default Roles",
        info: "Use predefined roles for technicians with preset access levels.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "customRoles",
        label: "Custom Roles",
        info: "Create tailored roles with specific permissions for different team members.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "apiAccess",
        label: "API Access",
        info: "Programmatic access to Zecurit data and workflows via API.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
    ],
  },
  {
    section: "Security & Privacy",
    rows: [
      {
        key: "twoFactorAuth",
        label: "Two-Factor Authentication (Email & Mobile App Authenticator)",
        info: "Adds an extra layer of security via email-based or mobile app authenticator verification.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "ssoSaml",
        label: "SSO / SAML Authentication",
        info: "Single sign-on via SAML for centralized identity and access management.",
        type: "boolean",
        values: { Standard: false, Professional: false, Enterprise: true },
      },
      {
        key: "ipRestriction",
        label: "IP Restriction",
        info: "Limit access to the platform based on specific IP addresses or ranges.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "sessionSettings",
        label: "Session Settings",
        info: "Configure session timeout, idle duration and automatic logout.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
      {
        key: "agentProtection",
        label: "Agent Protection",
        info: "Prevent unauthorized users from uninstalling or tampering with the agent.",
        type: "boolean",
        values: { Standard: false, Professional: true, Enterprise: true },
      },
      {
        key: "activityLog",
        label: "Activity Log",
        info: "Maintain a detailed audit trail of all user and system activities.",
        type: "boolean",
        values: { Standard: true, Professional: true, Enterprise: true },
      },
    ],
  },
];

// Zecurit Remote Access - converted from the marketing site's HTML pricing table embed.
// RA only has two editions (Standard, Professional) - confirm these match
// pricingCatalog.devicePricingByEdition[].editionName exactly.
const raFeaturesData = [
  {
    section: "Plan Specifications",
    rows: [
      {
        key: "technicians",
        label: "Technicians (Users)",
        info: "Included technician licenses",
        type: "text",
        values: { Standard: "1", Professional: "1" },
      },
      {
        key: "devices",
        label: "Devices",
        info: "Maximum number of devices you can manage",
        type: "text",
        values: { Standard: "100", Professional: "200" },
      },
      {
        key: "concurrentSessions",
        label: "Concurrent Sessions",
        info: "Number of remote sessions that can run at the same time",
        type: "text",
        values: { Standard: "2", Professional: "4" },
      },
    ],
  },
  {
    section: "Included in Both Editions",
    rows: [
      {
        key: "unattendedRemoteAccess",
        label: "Unattended Remote Access",
        info: "Access devices remotely without user presence",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "onDemandRemoteSupport",
        label: "On-Demand Remote Support",
        info: "Provide on-demand remote assistance",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "fileTransfer",
        label: "File Transfer",
        info: "Transfer files during a remote session",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "chat",
        label: "Chat",
        info: "Chat with the end user during a session",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "multiMonitorSupport",
        label: "Multi-Monitor Support",
        info: "View and control multiple monitors during a session",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "clipboardSharing",
        label: "Clipboard Sharing",
        info: "Share clipboard contents between local and remote device",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "remoteRebootWol",
        label: "Remote Reboot & Wake-on-LAN",
        info: "Reboot or wake devices remotely",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "runAsSystemUser",
        label: "Run as System/User",
        info: "Run the remote session as system or logged-in user",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "remoteTaskManager",
        label: "Remote Task Manager",
        info: "Access task manager remotely",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "remoteCommandPrompt",
        label: "Remote Command Prompt",
        info: "Access command prompt remotely",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "windowsServiceManager",
        label: "Windows Service Manager",
        info: "Manage Windows services remotely",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "remoteRegistry",
        label: "Remote Registry",
        info: "Access the registry remotely",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "remoteEventViewer",
        label: "Remote Event Viewer",
        info: "Access event viewer remotely",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "remoteFileManager",
        label: "Remote File Manager",
        info: "Browse and manage files remotely",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "sessionConfirmation",
        label: "Session Confirmation",
        info: "Require end-user confirmation before a session starts",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "idleSessionTimeout",
        label: "Idle Session Timeout",
        info: "Automatically end sessions after a period of inactivity",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "inviteExternalUser",
        label: "Invite External User (Collaboration)",
        info: "Invite an external user to join a session",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "multiFactorAuth",
        label: "Multi-Factor Authentication (TFA)",
        info: "Secure logins with multi-factor authentication",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
    ],
  },
  {
    section: "Professional Exclusive",
    rows: [
      {
        key: "sessionRecording",
        label: "Session Recording",
        info: "Record remote control sessions",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "sessionAuditLogs",
        label: "Session Audit Logs",
        info: "View a log of session activity",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "multiTechnicianSupport",
        label: "Multi-Technician Support",
        info: "Support multiple technicians",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "rbac",
        label: "Role-Based Access Control (RBAC)",
        info: "Create custom roles and permissions",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "deviceGroups",
        label: "Device Groups",
        info: "Organize devices into groups for easier management",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "bulkDeployment",
        label: "Bulk Deployment",
        info: "Enroll devices in bulk via existing tools",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "sessionReports",
        label: "Session Reports",
        info: "View reports on remote sessions",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "scheduledReports",
        label: "Scheduled Reports",
        info: "Schedule automatic report generation",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "scheduledShutdownWake",
        label: "Scheduled Shutdown / Wake",
        info: "Schedule device shutdown and wake times",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "screenRecording",
        label: "Screen Recording",
        info: "Record the remote screen during a session",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "ssoSaml",
        label: "SSO/SAML Authentication",
        info: "Single sign-on via SAML",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
    ],
  },
];

// Zecurit Asset Manager - converted from the marketing site's HTML pricing table embed.
// `values` keys must match plan.planName exactly - confirm AM's pricing catalog actually
// returns editionName "Free" / "Standard" / "Professional" (adjust keys here if the
// catalog uses different names, e.g. "Free Edition").
const amFeaturesData = [
  {
    section: null,
    rows: [
      {
        key: "devices",
        label: "Devices",
        info: "Maximum number of endpoint devices that can be managed under this plan.",
        type: "text",
        values: {
          Free: "Up to 25 Endpoints",
          Standard: "> 25 Endpoints",
          Professional: "> 25 Endpoints",
        },
      },
      {
        key: "technician",
        label: "Technician",
        info: "Number of technicians that can access and manage the platform.",
        type: "text",
        values: {
          Free: "1 Tech",
          Standard: "> 1 Tech",
          Professional: "> 1 Tech",
        },
      },
    ],
  },
  {
    section: "Enrollment",
    rows: [
      {
        key: "manualDeployment",
        label: "Manual Deployment",
        info: "Manually install the agent on individual devices.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
      {
        key: "deploymentLink",
        label: "Deployment Link",
        info: "Shareable link to initiate device enrollment remotely.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
      {
        key: "domainAuth",
        label: "Domain Authentication",
        info: "Use domain user credentials to authenticate and enroll devices via Zecurit Connector",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
      {
        key: "bulkDeployment",
        label: "Bulk Deployment (Entra ID, GPO, SCCM, Intune)",
        info: "Perform bulk agent deployment across multiple devices using enterprise tools like Azure AD, GPO, SCCM or Intune.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
    ],
  },
  {
    section: "Inventory",
    rows: [
      {
        key: "assetDiscovery",
        label: "Asset Discovery",
        info: "Automatically scan and identify all connected hardware and software assets.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
      {
        key: "softwareInventory",
        label: "Software Inventory Management",
        info: "View and manage a list of all installed software across devices.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
      {
        key: "hardwareInventory",
        label: "Hardware Inventory Management",
        info: "Track and manage hardware components and configurations.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
      {
        key: "softwareLicense",
        label: "Software License Management",
        info: "Monitor and track software license usage and compliance.",
        type: "boolean",
        values: { Free: true, Standard: false, Professional: true },
      },
      {
        key: "softwareMetering",
        label: "Software Metering",
        info: "Monitor the frequency and duration of software usage to optimize licenses.",
        type: "boolean",
        values: { Free: true, Standard: false, Professional: true },
      },
      {
        key: "blacklistWhitelist",
        label: "Blacklist and Whitelist Software",
        info: "Define and manage restricted (blacklisted) and allowed (whitelisted) software.",
        type: "boolean",
        values: { Free: true, Standard: false, Professional: true },
      },
      {
        key: "softwareCategory",
        label: "Software Category",
        info: "Classify software based on type (e.g., productivity, security, games).",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
      {
        key: "geoLocationTracking",
        label: "Geo Location Tracking",
        info: "Track the physical location of enrolled devices.",
        type: "boolean",
        values: { Free: true, Standard: false, Professional: true },
      },
      {
        key: "alertsPolicy",
        label: "Alerts Policy",
        info: "Configure rules to trigger alerts based on inventory or policy violations.",
        type: "boolean",
        values: { Free: true, Standard: false, Professional: true },
      },
      {
        key: "scheduleScan",
        label: "Schedule Scan",
        info: "Perform schedule device scanning at predefined intervals for asset updates.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
    ],
  },
  {
    section: "Reports",
    rows: [
      {
        key: "securityReports",
        label: "Security Reports",
        info: "Generate insights on vulnerabilities, threats and compliance status.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
      {
        key: "hardwareReports",
        label: "Hardware Reports",
        info: "Reports covering hardware configurations changes and statuses.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
      {
        key: "softwareReports",
        label: "Software Reports",
        info: "Reports summarizing installed software and usage statistics.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
      {
        key: "licenseReports",
        label: "License Reports",
        info: "Overview of license usage, expiry, and compliance across the organization.",
        type: "boolean",
        values: { Free: true, Standard: false, Professional: true },
      },
      {
        key: "certificateReports",
        label: "Certificate Reports",
        info: "View certificates installed on devices and monitor expiry.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
      {
        key: "userLogonReports",
        label: "User Logon Reports",
        info: "Track user login activity across managed devices.",
        type: "boolean",
        values: { Free: true, Standard: false, Professional: true },
      },
      {
        key: "enrollmentReports",
        label: "Enrollment Reports",
        info: "Summary of device enrollment activity, including methods and statuses.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
      {
        key: "powerReports",
        label: "Power Reports",
        info: "Analyze device power usage and sleep/wake patterns.",
        type: "boolean",
        values: { Free: true, Standard: false, Professional: true },
      },
      {
        key: "scheduleReports",
        label: "Schedule Reports",
        info: "Automate generation and delivery of reports at regular intervals.",
        type: "boolean",
        values: { Free: false, Standard: false, Professional: true },
      },
    ],
  },
  {
    section: "Administration & Control",
    rows: [
      {
        key: "deviceGroups",
        label: "Device Groups",
        info: "Organize devices into groups for easier policy and task management.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
      {
        key: "rebranding",
        label: "Rebranding",
        info: "Customize the agent interface and branding elements to reflect your organization.",
        type: "boolean",
        values: { Free: false, Standard: false, Professional: true },
      },
      {
        key: "multiTechnician",
        label: "Multi-technician",
        info: "Allow multiple technicians to access the platform simultaneously.",
        type: "boolean",
        values: { Free: false, Standard: true, Professional: true },
      },
      {
        key: "defaultRoles",
        label: "Default Roles",
        info: "Use predefined roles for technicians with preset access levels.",
        type: "boolean",
        values: { Free: false, Standard: true, Professional: true },
      },
      {
        key: "customRoles",
        label: "Custom Roles",
        info: "Create tailored roles with specific permissions for different team members.",
        type: "boolean",
        values: { Free: false, Standard: true, Professional: true },
      },
    ],
  },
  {
    section: "Security & Privacy",
    rows: [
      {
        key: "twoFactorAuth",
        label: "Two-factor authentication",
        info: "Adds an extra layer of security via email-based authentication.",
        type: "boolean",
        values: { Free: false, Standard: true, Professional: true },
      },
      {
        key: "ipRestriction",
        label: "IP Restriction",
        info: "Limit access to the platform based on specific IP addresses or ranges.",
        type: "boolean",
        values: { Free: false, Standard: false, Professional: true },
      },
      {
        key: "sessionSettings",
        label: "Session Settings",
        info: "Configure session timeout, idle duration and automatic logout.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
      {
        key: "agentProtection",
        label: "Agent Protection",
        info: "Prevent unauthorized users from uninstalling or tampering with the agent.",
        type: "boolean",
        values: { Free: false, Standard: false, Professional: true },
      },
      {
        key: "activityLog",
        label: "Activity Log",
        info: "Maintain a detailed audit trail of all user and system activities.",
        type: "boolean",
        values: { Free: true, Standard: true, Professional: true },
      },
    ],
  },
];
const vmFeaturesData = [
  {
    section: null,
    rows: [
      {
        key: "devices",
        label: "Devices",
        info: "Maximum number of endpoint devices that can be managed under this plan.",
        type: "text",
        values: { Standard: "> 15 Endpoints", Professional: "> 15 Endpoints" },
      },
      {
        key: "technician",
        label: "Technician",
        info: "Number of technicians that can access and manage the platform.",
        type: "text",
        values: { Standard: "> 1 Tech", Professional: "> 1 Tech" },
      },
    ],
  },
  {
    section: "Enrollment",
    rows: [
      {
        key: "manualDeployment",
        label: "Manual Deployment",
        info: "Manually install the agent on individual devices.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "deploymentLink",
        label: "Deployment Link",
        info: "Shareable link to initiate device enrollment remotely.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "domainAuth",
        label: "Domain Authentication",
        info: "Use domain user credentials to authenticate and enroll devices via Zecurit Connector",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "bulkDeployment",
        label: "Bulk Deployment (Entra ID, GPO, SCCM, Intune)",
        info: "Perform bulk agent deployment across multiple devices using enterprise tools like Azure AD, GPO, SCCM or Intune.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
    ],
  },
  {
    section: "Vulnerability & Risk Management",
    rows: [
      {
        key: "vulnerabilityManagement",
        label: "Vulnerability Management",
        info: "Detect, prioritize, and track software and OS vulnerabilities across managed endpoints.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "highRiskSoftware",
        label: "High Risk Software Detection",
        info: "Identify software with known high-risk vulnerabilities or end-of-life status.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "portAudit",
        label: "Port Audit",
        info: "Audit open ports across endpoints to identify unnecessary exposure and risk. Included with Enterprise; feature launch coming soon.",
        type: "boolean",
        values: { Standard: false, Professional: true },
        comingSoon: { Enterprise: true },
      },
      {
        key: "zeroDayAlerts",
        label: "Zero-Day Vulnerability Alerts",
        info: "Receive proactive alerts on newly disclosed zero-day vulnerabilities affecting your environment.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
    ],
  },
  {
    section: "Patch Management",
    rows: [
      // { key: "windowsUpdatePolicy", label: "Windows Update Policy", info: "Create and manage Windows update policies for automated patching.", type: "boolean", values: { Standard: true, Professional: true } },
      {
        key: "missingInstalledPatches",
        label: "Missing & Installed Patches",
        info: "View missing and installed patches for individual devices.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "orgLevelPatchViews",
        label: "Org Level Patch Views",
        info: "Organization-wide view of missing, installed, and available patches.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "patchDeployment",
        label: "Patch Deployment",
        info: "Deploy patches to selected devices or groups.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "automatedPatchDeployment",
        label: "Automated Patch Deployment",
        info: "Automatically deploy approved patches to devices based on policy, without manual intervention.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "testApprovePatch",
        label: "Test and Approve Patch",
        info: "Test patches in a controlled group before approving them for org-wide deployment.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
    ],
  },
  {
    section: "Reports",
    rows: [
      {
        key: "customReports",
        label: "Custom Reports (Build Reports Your Way)",
        info: "Create tailored reports by selecting the data, filters, and columns that matter most to your organization, and export them for analysis and sharing.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "scheduleReports",
        label: "Schedule Reports",
        info: "Automate generation and delivery of reports at regular intervals.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "patchReports",
        label: "Patch Reports",
        info: "Detailed reporting on patch test, approval, and deployment status across the organization.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "vulnerabilityReports",
        label: "Vulnerability Reports",
        info: "Reports summarizing detected vulnerabilities, risk levels, and remediation status across endpoints.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
    ],
  },
  {
    section: "Administration & Control",
    rows: [
      {
        key: "deviceGroups",
        label: "Device Groups (Static & Unique)",
        info: "Organize devices into groups for easier policy and task management.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "rebranding",
        label: "Rebranding",
        info: "Customize the agent interface and branding elements to reflect your organization.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "multiTechnician",
        label: "Multi-technician",
        info: "Allow multiple technicians to access the platform simultaneously.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "defaultRoles",
        label: "Default Roles",
        info: "Use predefined roles for technicians with preset access levels.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "customRoles",
        label: "Custom Roles",
        info: "Create tailored roles with specific permissions for different team members.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "apiAccess",
        label: "API Access",
        info: "Programmatic access to Zecurit data and workflows via API.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
    ],
  },
  {
    section: "Security & Privacy",
    rows: [
      {
        key: "twoFactorAuth",
        label: "Two-Factor Authentication (Email & Mobile App Authenticator)",
        info: "Adds an extra layer of security via email-based or mobile app authenticator verification.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "ssoSaml",
        label: "SSO / SAML Authentication",
        info: "Single sign-on via SAML for centralized identity and access management.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "ipRestriction",
        label: "IP Restriction",
        info: "Limit access to the platform based on specific IP addresses or ranges.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "sessionSettings",
        label: "Session Settings",
        info: "Configure session timeout, idle duration and automatic logout.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "agentProtection",
        label: "Agent Protection",
        info: "Prevent unauthorized users from uninstalling or tampering with the agent.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "activityLog",
        label: "Activity Log",
        info: "Maintain a detailed audit trail of all user and system activities.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
    ],
  },
  {
    section: "Remote Access Add on",
    rows: [
      {
        key: "remoteAccessAddOn",
        label: "Remote Access & Tools",
        info: "Remote control, file transfer, chat and other remote support tools, available as an add-on.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
    ],
  },
];

const pmFeaturesData = [
  {
    section: null,
    rows: [
      {
        key: "devices",
        label: "Devices",
        info: "Maximum number of endpoint devices that can be managed under this plan.",
        type: "text",
        values: { Standard: "> 15 Endpoints", Professional: "> 15 Endpoints" },
      },
      {
        key: "technician",
        label: "Technician",
        info: "Number of technicians that can access and manage the platform.",
        type: "text",
        values: { Standard: "> 1 Tech", Professional: "> 1 Tech" },
      },
    ],
  },
  {
    section: "Enrollment",
    rows: [
      {
        key: "manualDeployment",
        label: "Manual Deployment",
        info: "Manually install the agent on individual devices.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "deploymentLink",
        label: "Deployment Link",
        info: "Shareable link to initiate device enrollment remotely.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "domainAuth",
        label: "Domain Authentication",
        info: "Use domain user credentials to authenticate and enroll devices via Zecurit Connector",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "bulkDeployment",
        label: "Bulk Deployment (Entra ID, GPO, SCCM, Intune)",
        info: "Perform bulk agent deployment across multiple devices using enterprise tools like Azure AD, GPO, SCCM or Intune.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
    ],
  },
  {
    section: "Patch Management",
    rows: [
      // { key: "windowsUpdatePolicy", label: "Windows Update Policy", info: "Create and manage Windows update policies for automated patching.", type: "boolean", values: { Standard: true, Professional: true } },
      {
        key: "missingInstalledPatches",
        label: "Missing & Installed Patches",
        info: "View missing and installed patches for individual devices.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "orgLevelPatchViews",
        label: "Org Level Patch Views",
        info: "Organization-wide view of missing, installed, and available patches.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "patchDeployment",
        label: "Patch Deployment",
        info: "Deploy patches to selected devices or groups.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "automatedPatchDeployment",
        label: "Automated Patch Deployment",
        info: "Automatically deploy approved patches to devices based on policy, without manual intervention.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "testApprovePatch",
        label: "Test and Approve Patch",
        info: "Test patches in a controlled group before approving them for org-wide deployment.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
    ],
  },
  {
    section: "Reports",
    rows: [
      {
        key: "customReports",
        label: "Custom Reports (Build Reports Your Way)",
        info: "Create tailored reports by selecting the data, filters, and columns that matter most to your organization, and export them for analysis and sharing.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "scheduleReports",
        label: "Schedule Reports",
        info: "Automate generation and delivery of reports at regular intervals.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "patchReports",
        label: "Patch Reports",
        info: "Detailed reporting on patch test, approval, and deployment status across the organization.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
    ],
  },
  {
    section: "Administration & Control",
    rows: [
      {
        key: "deviceGroups",
        label: "Device Groups (Static & Unique)",
        info: "Organize devices into groups for easier policy and task management.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "rebranding",
        label: "Rebranding",
        info: "Customize the agent interface and branding elements to reflect your organization.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "multiTechnician",
        label: "Multi-technician",
        info: "Allow multiple technicians to access the platform simultaneously.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "defaultRoles",
        label: "Default Roles",
        info: "Use predefined roles for technicians with preset access levels.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "customRoles",
        label: "Custom Roles",
        info: "Create tailored roles with specific permissions for different team members.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "apiAccess",
        label: "API Access",
        info: "Programmatic access to Zecurit data and workflows via API.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
    ],
  },
  {
    section: "Security & Privacy",
    rows: [
      {
        key: "twoFactorAuth",
        label: "Two-Factor Authentication (Email & Mobile App Authenticator)",
        info: "Adds an extra layer of security via email-based or mobile app authenticator verification.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "ssoSaml",
        label: "SSO / SAML Authentication",
        info: "Single sign-on via SAML for centralized identity and access management.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "ipRestriction",
        label: "IP Restriction",
        info: "Limit access to the platform based on specific IP addresses or ranges.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "sessionSettings",
        label: "Session Settings",
        info: "Configure session timeout, idle duration and automatic logout.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
      {
        key: "agentProtection",
        label: "Agent Protection",
        info: "Prevent unauthorized users from uninstalling or tampering with the agent.",
        type: "boolean",
        values: { Standard: false, Professional: true },
      },
      {
        key: "activityLog",
        label: "Activity Log",
        info: "Maintain a detailed audit trail of all user and system activities.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
    ],
  },
  {
    section: "Remote Access Add on",
    rows: [
      {
        key: "remoteAccessAddOn",
        label: "Remote Access & Tools",
        info: "Remote control, file transfer, chat and other remote support tools, available as an add-on.",
        type: "boolean",
        values: { Standard: true, Professional: true },
      },
    ],
  },
];

const compareFeaturesDataByProduct = {
  em: emFeaturesData,
  ra: raFeaturesData,
  am: amFeaturesData,
  vm: vmFeaturesData,
  pm: pmFeaturesData,
};

export const getCompareFeaturesData = (product) =>
  compareFeaturesDataByProduct[product] || compareFeaturesDataByProduct.em;

export default compareFeaturesDataByProduct;
